/**
 * Prompt Shaper — make generation prompts SHAPED BY THIS AUTHOR'S HISTORY,
 * with provenance receipts naming exactly what shaped the draft.
 *
 * Fixes the auditor's "prompt not shaped by history" finding: drafts were
 * generated from the brief + generic constraints while the What-Works
 * signal engine sat unused. `shapePrompt` blends the two knowledge sources
 * with a transparent weight:
 *
 *   personalWeight = coldStartWeight(totalPosts)   // 0 → 1 over 20 posts
 *
 *   zero history  → personalWeight 0, NO personal signals injected,
 *                   the prompt leans 100% on the retrieved playbook rules
 *   real history  → evidenced signals (strength weak/strong only — 'none'
 *                   is never claimed) injected alongside the rules
 *
 * `toPromptProvenance` turns a shaped prompt into a receipt naming the
 * EXACT signal + rule ids that shaped it (digest via the same FNV-1a seal
 * the Decision Ledger chain uses). `generateWithWorker` runs the shaped
 * prompt through a REAL injected worker and degrades to a deterministic
 * template fallback when the worker is absent or throws — so the gate can
 * exercise the whole path offline.
 *
 * PURE by contract except `generateWithWorker`, whose only side effect is
 * the single injected `worker.run` call. No store, no firebase, no clock.
 */

import { coldStartWeight, type Signal, type SignalSet } from './whatWorks';
import { retrievePlaybookRules, type PlaybookRule } from './playbookLoader';
import { fnvHash } from '../ledger/verifyChain';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** A generation prompt plus the receipt of everything that shaped it. */
export type ShapedPrompt = {
  system: string;
  user: string;
  /** Evidenced per-author signals injected into the prompt (never 'none'). */
  injectedSignals: Signal[];
  /** Retrieved playbook rules injected into the prompt. */
  injectedRules: PlaybookRule[];
  /** 0..1 — how much the prompt leans on personal history vs the playbook. */
  personalWeight: number;
};

/** The provenance receipt for one shaped prompt. */
export type PromptProvenance = {
  /** Exact injected signal ids, `dimension:value`. */
  signalIds: string[];
  /** Exact injected playbook rule ids. */
  ruleIds: string[];
  personalWeight: number;
  /** Human-readable one-liner for the UI chip. */
  summary: string;
  /** Deterministic FNV-1a digest of the receipt (same seal as the ledger). */
  digest: string;
};

/** How many evidenced signals a prompt injects at most. */
export const MAX_INJECTED_SIGNALS = 6;

/** How many playbook rules a prompt retrieves. */
export const MAX_INJECTED_RULES = 5;

/** Stable id for a signal — used in prompts and provenance receipts. */
export function signalId(s: Signal): string {
  return `${s.dimension}:${s.value}`;
}

// ---------------------------------------------------------------------------
// Shaping
// ---------------------------------------------------------------------------

const round2 = (n: number) => Math.round(n * 100) / 100;

/**
 * Blend per-author signals with retrieved playbook rules into one shaped
 * prompt. Only evidenced signals (strength 'weak' or 'strong') are ever
 * injected, and NONE are injected at zero history — the cold-start prompt
 * honestly leans 100% on the playbook. Deterministic: same inputs, same
 * strings.
 */
export function shapePrompt(input: {
  idea: string;
  format: string;
  signalSet: SignalSet;
  playbook: PlaybookRule[];
  totalPosts: number;
}): ShapedPrompt {
  const personalWeight = round2(coldStartWeight(input.totalPosts));

  const injectedSignals =
    personalWeight > 0
      ? input.signalSet.signals
          .filter((s) => s.strength !== 'none')
          .slice(0, MAX_INJECTED_SIGNALS)
      : [];

  const injectedRules = retrievePlaybookRules(
    input.playbook,
    { format: input.format, topic: input.idea },
    MAX_INJECTED_RULES,
  );

  const parts: string[] = [
    `You are the ArgoIQ Content Engine writing a ${input.format} shaped by this author's real posting history.`,
    '',
    `Personal-history weight: ${personalWeight} (0 = playbook only, 1 = fully personalized).`,
  ];

  if (injectedRules.length > 0) {
    parts.push('', 'Playbook rules (field-tested best practices — follow each):');
    for (const r of injectedRules) {
      parts.push(`- [${r.id}] ${r.title}: ${r.text}`);
    }
  }

  if (injectedSignals.length > 0) {
    parts.push(
      '',
      `What works for THIS author (observed from ${input.signalSet.totalPosts} logged posts — weight these by the personal-history weight above):`,
    );
    for (const s of injectedSignals) {
      parts.push(
        `- [${signalId(s)}] ${s.dimension} "${s.value}" performs at ${s.lift}x this author's average (n=${s.sampleSize}, ${s.strength} evidence)`,
      );
    }
  } else {
    parts.push(
      '',
      'No evidenced personal signals yet — rely entirely on the playbook rules above.',
    );
  }

  parts.push(
    '',
    'Output ONLY the finished draft text. No preamble, no commentary, no markdown fences.',
  );

  return {
    system: parts.join('\n'),
    user: `Write the ${input.format} for this idea:\n${input.idea.trim()}`,
    injectedSignals,
    injectedRules,
    personalWeight,
  };
}

// ---------------------------------------------------------------------------
// Provenance receipt
// ---------------------------------------------------------------------------

/**
 * The receipt: exactly which signals and rules shaped this prompt, at what
 * personal weight, sealed with the same FNV-1a digest style the Decision
 * Ledger chain uses. Deterministic — same shaped prompt, same digest.
 */
export function toPromptProvenance(p: ShapedPrompt): PromptProvenance {
  const signalIds = p.injectedSignals.map(signalId);
  const ruleIds = p.injectedRules.map((r) => r.id);
  const summary =
    `${signalIds.length} personal signal${signalIds.length === 1 ? '' : 's'} + ` +
    `${ruleIds.length} playbook rule${ruleIds.length === 1 ? '' : 's'} · ` +
    `${Math.round(p.personalWeight * 100)}% personalized`;
  const digest = fnvHash(
    '',
    JSON.stringify([signalIds, ruleIds, p.personalWeight]),
  );
  return { signalIds, ruleIds, personalWeight: p.personalWeight, summary, digest };
}

// ---------------------------------------------------------------------------
// Generation — real worker with deterministic fallback
// ---------------------------------------------------------------------------

/**
 * Run a shaped prompt through the injected REAL worker; when the worker is
 * absent, throws, or returns blank text, fall back to the caller-supplied
 * deterministic template. The result always says honestly which path
 * produced the text. The only side effect is the single `worker.run` call.
 */
export async function generateWithWorker(
  p: ShapedPrompt,
  worker: { run: (prompt: string) => Promise<string> } | null | undefined,
  deterministicFallback: (p: ShapedPrompt) => string,
): Promise<{ text: string; source: 'worker' | 'fallback' }> {
  if (worker) {
    try {
      const text = await worker.run(`${p.system}\n\n${p.user}`);
      if (typeof text === 'string' && text.trim()) {
        return { text, source: 'worker' };
      }
    } catch {
      // Fall through to the deterministic fallback — never dead, never fake.
    }
  }
  return { text: deterministicFallback(p), source: 'fallback' };
}

/**
 * The shipped deterministic fallback: a template draft assembled ONLY from
 * the shaped prompt's own inputs (idea, format, injected rules + signals) —
 * no fabricated facts, stable output for stable input. Exported so the UI
 * and tests share one fallback instead of inventing their own.
 */
export function deterministicFallbackDraft(p: ShapedPrompt): string {
  const idea = p.user.split('\n').slice(1).join('\n').trim() || 'this idea';
  const lines: string[] = [];

  const hookSignal = p.injectedSignals.find((s) => s.dimension === 'hookStyle');
  if (hookSignal?.value === 'question') {
    lines.push(`What actually changes when ${idea.replace(/\.$/, '')}?`);
  } else if (hookSignal?.value === 'number-led') {
    lines.push(`1 thing worth knowing: ${idea}`);
  } else {
    lines.push(`I keep coming back to this: ${idea}`);
  }

  lines.push('');
  lines.push('Here is the short version:');
  const ruleLines = p.injectedRules.slice(0, 3);
  if (ruleLines.length > 0) {
    for (const r of ruleLines) lines.push(`- ${r.title}`);
  } else {
    lines.push('- Lead with the specifics, not the setup.');
  }
  lines.push('');
  lines.push('Reply and tell me what you would add.');

  return lines.join('\n');
}
